import { WITNESS_API_BASE } from './console-env';
import { tenantAuthorizationHeader } from './tenant-auth';
import { ApiNotConfiguredError, type CeremonySummary, type CreateCeremonyInput } from './witness-api';

export interface ClioShell {
  remoteObject: { id: string; reference: { remote: string; remoteTenantId: string;
    remoteObjectType: string; remoteObjectId: string }; createdAt: number };
  latestCeremony: (Omit<CeremonySummary, 'state'> & { state: string }) | null;
  syncError: { ceremonyId: string; occurredAtMs: number } | null;
}
export interface ClioEntry { tenantId: string; shell: Omit<ClioShell, 'syncError'> }

export async function enterClioCeremony(params: URLSearchParams): Promise<ClioEntry> {
  const names = ['custom_action_id', 'user_id', 'subject_url', 'custom_action_nonce'];
  const launch = Object.fromEntries(names.map((name) => [name, params.get(name)]));
  return request('/actions/ceremony', { method: 'POST', body: JSON.stringify(launch) });
}

export async function getClioShell(tenantId: string, remoteObjectId: string): Promise<{ shell: ClioShell }> {
  return request(objectPath(tenantId, remoteObjectId));
}

export interface ClioContactPrefill { matterNumber: string; contactId: string; name: string; type: string }

export async function getClioPrefill(tenantId: string, remoteObjectId: string) {
  return request<{ contact: ClioContactPrefill | null }>(`${objectPath(tenantId, remoteObjectId)}/prefill`);
}

export async function createClioCeremony(tenantId: string, remoteObjectId: string, input: CreateCeremonyInput): Promise<{
  ceremony: { ceremonyId: string; createdAt: number };
}> {
  return request(`${objectPath(tenantId, remoteObjectId)}/ceremonies`, { method: 'POST', body: JSON.stringify({
    documentInputs: input.documentInputs,
    templateId: input.templateId, subject: { name: input.subjectName }, deliveryEmail: input.deliveryEmail,
  }) });
}

function objectPath(tenantId: string, remoteObjectId: string) {
  return `/tenants/${encodeURIComponent(tenantId)}/remote-objects/${encodeURIComponent(remoteObjectId)}`;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  if (!WITNESS_API_BASE) throw new ApiNotConfiguredError();
  const response = await fetch(`${WITNESS_API_BASE}/integrations/clio${path}`, {
    ...init, headers: { ...await tenantAuthorizationHeader(), ...(init.body ? { 'Content-Type': 'application/json' } : {}) },
  });
  const body = await response.json();
  if (!response.ok) throw new Error(clioErrorMessage(body.error, response.status));
  return body as T;
}

function clioErrorMessage(code: string, status: number): string {
  if (code === 'nonce_validation_required') return 'A new Clio action is required to create another ceremony.';
  if (code === 'invalid_nonce') return 'This Clio action has expired or already been used.';
  if (status === 401) return 'Your Witness session has expired. Sign in again.';
  if (status === 403) return 'This Clio action is not available to your signed-in Witness user.';
  if (status === 404) return 'This Matter’s Witness record was not found.';
  if (code === 'clio_not_ready') return 'The Clio connection is not ready.';
  if (code === 'invalid_creation_input') return 'The template rejected these inputs. Reload the form and check the values.';
  if (code === 'invalid_launch') return 'The Clio action is missing valid Matter information.';
  return 'The request failed. Please try again.';
}
